import React, {useContext, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import APIWrapperContext from '../../../services/APIWrapperContext';
import {DocumentsService} from '../../../services/openapi/services/DocumentsService';
import ContextViewFileList from '../../Contexts/ContextViewFileList';
import UserContext from '../../../Contexts/UserContext/UserContext';
import {FolderTreePicker} from '../MoveFile/FolderTreePicker';

interface CopyFolderProps {
    folderId: string;
    name: string;
}

export const CopyFolder: React.FC<CopyFolderProps> = ({folderId, name}) => {
    const {call_backend_api} = useContext(APIWrapperContext);
    const {curr_dir_id, update_dir_view, invalidate_dir_cache} = useContext(ContextViewFileList);
    const {user_roll} = useContext(UserContext);
    const [open, setOpen] = useState(false);
    const [copying, setCopying] = useState(false);
    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
    const [selectedFolderPath, setSelectedFolderPath] = useState<string>('');

    const isScopedHausverwaltungAdmin =
        (user_roll?.includes('Admin Hausverwaltung') ?? false) &&
        !(user_roll?.includes('Administrator') ?? false);

    const handleClose = () => {
        if (copying) {
            return;
        }
        setOpen(false);
        setSelectedFolderId(null);
        setSelectedFolderPath('');
    };

    const handleCopy = () => {
        if (!selectedFolderId) {
            return;
        }

        setCopying(true);
        call_backend_api(
            DocumentsService.copyFolderDocCopyFolderPost(folderId, selectedFolderId),
            () => {
                setCopying(false);
                setOpen(false);
                setSelectedFolderId(null);
                setSelectedFolderPath('');
                invalidate_dir_cache();
                update_dir_view(curr_dir_id);
            },
            () => {
                setCopying(false);
            },
            undefined,
            true,
            'Ordner kopiert!',
            'Ordner konnte nicht kopiert werden!'
        );
    };

    return (
        <>
            <Tooltip title='Ordner kopieren'>
                <IconButton
                    onClick={(event) => {
                        event.stopPropagation();
                        setOpen(true);
                    }}
                    color='primary'
                    aria-label='copy-folder'>
                    <ContentCopyIcon/>
                </IconButton>
            </Tooltip>
            <Dialog
                open={open}
                onClose={handleClose}
                onClick={(event) => event.stopPropagation()}
                maxWidth='sm'
                fullWidth
                aria-labelledby='copy-folder-dialog-title'
                aria-describedby='copy-folder-dialog-description'>
                <DialogTitle id='copy-folder-dialog-title'>Ordner kopieren</DialogTitle>
                <DialogContent>
                    <DialogContentText id='copy-folder-dialog-description' sx={{mb: 2}}>
                        Wählen Sie den Zielordner für „{name}“. Der Ordner und alle
                        enthaltenen Dateien und Unterordner werden kopiert.
                    </DialogContentText>

                    {selectedFolderId && (
                        <Box sx={{mb: 2, p: 1, bgcolor: 'primary.light', borderRadius: 1}}>
                            <Typography variant='body2' color='primary.contrastText'>
                                Kopieren nach „{selectedFolderPath}“
                            </Typography>
                        </Box>
                    )}

                    <FolderTreePicker
                        selectedFolderId={selectedFolderId}
                        onSelectFolder={(folderId, _folderName, folderPath) => {
                            setSelectedFolderId(folderId);
                            setSelectedFolderPath(folderPath);
                        }}
                        currentFileId={folderId}
                        allowedRootPrefix={
                            isScopedHausverwaltungAdmin ? 'Hausverwaltung/' : undefined
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={copying}>Abbrechen</Button>
                    <Button
                        onClick={handleCopy}
                        color='primary'
                        variant='contained'
                        disabled={!selectedFolderId || copying}
                        aria-busy={copying}
                        startIcon={
                            copying ? <CircularProgress size={16} color='inherit'/> : undefined
                        }
                        sx={{minWidth: 128}}
                        autoFocus>
                        {copying ? 'Wird kopiert…' : 'Kopieren'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
